import { apiFetch } from "@/lib/api";
import { GAIL_API } from "@/utils/common/constants/routes";

export type UserAcknowledgementStatus = "seen" | "dismissed" | "completed" | "skipped";

export interface UserAcknowledgementDescriptor {
  scope: string;
  key: string;
  type: string;
  version: string;
}

export interface UserAcknowledgement extends UserAcknowledgementDescriptor {
  status: UserAcknowledgementStatus;
  firstAcknowledgedAt: string;
  lastAcknowledgedAt: string;
  metadata?: Record<string, unknown> | null;
}

interface UserAcknowledgementsResponse {
  acknowledgements: UserAcknowledgement[];
}

export const USER_ACKNOWLEDGEMENT_TYPES = {
  tour: "tour",
  banner: "banner",
  releaseNote: "release_note",
  alert: "alert",
  announcement: "announcement",
} as const;

export const GAILGPT_WELCOME_TOUR_ACKNOWLEDGEMENT = {
  scope: "gailgpt",
  type: USER_ACKNOWLEDGEMENT_TYPES.tour,
  key: "welcome",
  version: "v1",
} as const satisfies UserAcknowledgementDescriptor;

export const DASHBOARD_TOUR_ACKNOWLEDGEMENT = {
  scope: "dashboard",
  type: USER_ACKNOWLEDGEMENT_TYPES.tour,
  key: "overview",
  version: "v1",
} as const satisfies UserAcknowledgementDescriptor;

export const ASSISTANTS_LIST_TOUR_ACKNOWLEDGEMENT = {
  scope: "assistants",
  type: USER_ACKNOWLEDGEMENT_TYPES.tour,
  key: "list",
  version: "v2",
} as const satisfies UserAcknowledgementDescriptor;

export const ASSISTANT_GENERAL_TOUR_ACKNOWLEDGEMENT = {
  scope: "assistants",
  type: USER_ACKNOWLEDGEMENT_TYPES.tour,
  key: "general",
  version: "v1",
} as const satisfies UserAcknowledgementDescriptor;

export const EVALS_TOUR_ACKNOWLEDGEMENT = {
  scope: "evals",
  type: USER_ACKNOWLEDGEMENT_TYPES.tour,
  key: "welcome",
  version: "v3",
} as const satisfies UserAcknowledgementDescriptor;

export const WORKFLOWS_TOUR_ACKNOWLEDGEMENT = {
  scope: "workflows",
  type: USER_ACKNOWLEDGEMENT_TYPES.tour,
  key: "welcome",
  version: "v1",
} as const satisfies UserAcknowledgementDescriptor;

export const buildBannerAcknowledgement = (
  bannerId: string
): UserAcknowledgementDescriptor => ({
  scope: "global",
  type: USER_ACKNOWLEDGEMENT_TYPES.banner,
  key: bannerId,
  version: "v1",
});

export const buildCannyChangelogAcknowledgement = (
  entryId: string
): UserAcknowledgementDescriptor => ({
  scope: "canny",
  type: USER_ACKNOWLEDGEMENT_TYPES.releaseNote,
  key: entryId,
  version: "v1",
});

export const buildEodReportAcknowledgement = (
  reportId: string
): UserAcknowledgementDescriptor => ({
  scope: "reporting",
  type: USER_ACKNOWLEDGEMENT_TYPES.alert,
  key: reportId,
  version: "v1",
});

const CACHE_PREFIX = "gail_acknowledgement";

const normalize = (value: string) => value.trim().toLowerCase();

const storagePart = (value: string) => encodeURIComponent(normalize(value));

export function buildUserAcknowledgementId(descriptor: UserAcknowledgementDescriptor): string {
  return [
    normalize(descriptor.scope),
    normalize(descriptor.type),
    normalize(descriptor.key),
    normalize(descriptor.version),
  ].join(":");
}

export function buildUserAcknowledgementCacheKey(
  descriptor: UserAcknowledgementDescriptor,
  orgId: string,
  userId: string
): string {
  return [
    CACHE_PREFIX,
    storagePart(orgId),
    storagePart(userId),
    storagePart(descriptor.scope),
    storagePart(descriptor.type),
    storagePart(descriptor.key),
    storagePart(descriptor.version),
  ].join("_");
}

export function buildLegacyGailGptWelcomeTourStorageKey(orgId: string, userId: string): string {
  return `gail_onboarding_seen_${orgId}_${userId}`;
}

export function readCachedAcknowledgement(
  descriptor: UserAcknowledgementDescriptor,
  orgId: string,
  userId: string
): UserAcknowledgement | null {
  if (globalThis.window === undefined) return null;

  try {
    const cached = globalThis.localStorage.getItem(
      buildUserAcknowledgementCacheKey(descriptor, orgId, userId)
    );
    if (!cached) return null;

    const parsed = JSON.parse(cached) as UserAcknowledgement;
    return parsed && buildUserAcknowledgementId(parsed) === buildUserAcknowledgementId(descriptor)
      ? parsed
      : null;
  } catch {
    return null;
  }
}

export function writeCachedAcknowledgement(
  descriptor: UserAcknowledgementDescriptor,
  orgId: string,
  userId: string,
  acknowledgement: UserAcknowledgement
): void {
  if (globalThis.window === undefined) return;

  try {
    globalThis.localStorage.setItem(
      buildUserAcknowledgementCacheKey(descriptor, orgId, userId),
      JSON.stringify(acknowledgement)
    );
  } catch {
    // Cache writes are best-effort; the server acknowledgement is authoritative.
  }
}

export function hasLegacyAcknowledgement(keys: string[]): boolean {
  if (globalThis.window === undefined) return false;

  return keys.some((key) => {
    try {
      return globalThis.localStorage.getItem(key) === "true";
    } catch {
      return false;
    }
  });
}

export function findUserAcknowledgement(
  acknowledgements: UserAcknowledgement[],
  descriptor: UserAcknowledgementDescriptor
): UserAcknowledgement | null {
  const id = buildUserAcknowledgementId(descriptor);
  return acknowledgements.find((acknowledgement) => buildUserAcknowledgementId(acknowledgement) === id) ?? null;
}

export async function getUserAcknowledgements(userId: string): Promise<UserAcknowledgement[]> {
  const response = await apiFetch<UserAcknowledgementsResponse>(
    `${GAIL_API}/.internal/users/${encodeURIComponent(userId)}/acknowledgements`,
    "GET",
    { silent: true }
  );

  return response?.acknowledgements ?? [];
}

export async function setUserAcknowledgement(
  userId: string,
  descriptor: UserAcknowledgementDescriptor,
  status: UserAcknowledgementStatus,
  metadata?: Record<string, unknown>
): Promise<UserAcknowledgement | null> {
  return apiFetch<UserAcknowledgement>(
    `${GAIL_API}/.internal/users/${encodeURIComponent(userId)}/acknowledgements`,
    "PUT",
    {
      body: {
        ...descriptor,
        status,
        metadata,
      },
      silent: true,
    },
  );
}
